import { TaskUpdateI } from "../../services/task/task.edit.service";
import { S25ItemI } from "../../pojo/S25ItemI";
import { S25Util } from "../../util/s25-util";
import { EventData } from "../../services/event.service";
import { Event as EventItem } from "../../pojo/Event";
import { TaskService, TaskChange } from "../../services/task/task.service";
import { Task } from "../../pojo/Task";
import { Event } from "../../pojo/Event";
import { Item } from "../../pojo/Item";
import { S25Const } from "../../util/s25-const";
import { TaskTiersService } from "../../services/task/task.tiers.service";
import TaskListTaskItem = Task.TaskListTaskItem;
import { ManageWaitlistTaskInfo } from "./manage.tasks.component";
import { EventService } from "../../services/event.service";

export namespace TaskUtil {
    export function findChangedContacts(resp: TaskUpdateI, data: { added?: S25ItemI[]; removed?: S25ItemI[] }) {
        const changes = {
            success: { added: [] as S25ItemI[], removed: [] as S25ItemI[] },
            failed: { added: [] as S25ItemI[], removed: [] as S25ItemI[] },
        };

        const allContacts = S25Util.array.flatten(resp.data.map((task) => task.contacts));
        const contacts = new Map(allContacts.map((contact) => [contact.contId, contact]));

        for (const added of data.added || []) {
            const contact = contacts.get(Number(added.itemId));
            if (contact) {
                added.notifyType = contact.notifyType;
                changes.success.added.push(added);
            } else {
                changes.failed.added.push(added);
            }
        }
        for (const removed of data.removed || []) {
            if (!contacts.has(Number(removed.itemId))) {
                changes.success.removed.push(removed);
            } else {
                changes.failed.removed.push(removed);
            }
        }

        return changes;
    }

    export async function getEventTasks(
        eventData: EventData,
        currentContactId: number,
    ): Promise<ManageWaitlistTaskInfo[]> {
        const firstReservationDateById: Record<number, string> = {};
        const isWorkflowChained = await TaskTiersService.isWorkflowChained();
        if (!eventData.approval) return [];

        return S25Util.array.forceArray(eventData.approval).map((task: Event.Workflow.Task) => {
            const typeInfo = TaskService.extractApprovalTypeInfo(task, currentContactId);
            const contacts = S25Util.array.forceArray(task.approval_contact);
            const currentContact = contacts.find((contact) => +contact.approval_contact_id === currentContactId);
            const flagged = currentContact?.read_state === "F" ? "Yes" : "";
            const firstDate = firstReservationDateById[task.approval_profile_id] ?? eventData.start_date;
            const assignedTo = TaskService.formTaskContactString(!!currentContact, currentContactId, task);

            const filterContactId = (obj: { approval_contact_id: string | number }): boolean => {
                return Number(obj.approval_contact_id) === currentContactId;
            };

            task.approval_contact = S25Util.array.forceArray(task.approval_contact);

            let requester = S25Util.propertyGetParentWithChildValue(eventData, "role_id", S25Const.requestorRole.event);
            let requesterId = parseInt(S25Util.propertyGetVal(requester, "contact_id"));

            let matches = (task.approval_contact ?? []).filter(filterContactId);
            let currentUserApprovalContact = matches.length ? matches[0] : null;

            let hasApproveDenyPerms = !!currentUserApprovalContact;

            const reservations: Record<string | number, string> = {};
            const profiles = S25Util.array.forceArray(eventData.profile);
            for (let profile of profiles) {
                for (let res of S25Util.array.forceArray(profile.reservation)) {
                    reservations[res.reservation_id] = res.reservation_start_dt;
                }
            }

            return {
                comment: strVar(task.approval_comments),
                id: task.approval_id, // task Id
                name: strVar(task.approval_name),
                state: TaskService.taskStateToStateText(task.approval_state, typeInfo.typeId),
                stateId: TaskService?.getTaskState(task),
                type: typeInfo,
                flagged: flagged,
                assignedTo: strVar(assignedTo),
                assignedToIds: contacts.map((contact) => contact.approval_contact_id),
                object: strVar(task.object_type_name),
                respondByDate: S25Util.date.parseDropTZ(task.respond_by),
                firstDate: S25Util.date.parseDropTZ(firstDate),
                eventName: eventData.event_name,
                eventId: eventData.event_id,
                eventDate: S25Util.date.parseDropTZ(reservations[task.approval_profile_id] || firstDate),
                item: {
                    itemId: task.object_id,
                    itemName: task.approval_name,
                    itemTypeId: task.object_type,
                },
                objectType: task.object_type,
                hasApproveDenyPerms: hasApproveDenyPerms,
                taskBlocked: task.task_blocked || "F",
                blockTask: task.blocking_task || [],
                workflowChained: isWorkflowChained,
                requesterId: requesterId,
                reservationId: task.approval_profile_id, // for waitlist
            };
        });
    }

    export function strVar(value: string) {
        return S25Util.escapeXml(S25Util.toStr(value), false);
    }

    export async function deniedCompeteTasks(
        eventIds: number[],
        competesData: Task.SpaceCompete[],
    ): Promise<TaskChange[]> {
        if (!competesData?.length) return [];

        const rawEvents = await EventService.getEventsInclude(eventIds, "workflow", true, false, true);
        const eventsData = S25Util.array.forceArray(rawEvents?.events?.event) ?? [];

        const taskItems = eventsData.flatMap((eventData: EventData) => eventData?.approval ?? []);

        const matchedTasks: TaskChange[] = taskItems.flatMap((item: EventItem.Workflow.Task) => {
            const matchItem = competesData.find(
                (c) =>
                    c.conflictRsrvId === item.approval_profile_id &&
                    c.spaceId === item.object_id &&
                    item.approval_state === Task.States.InProgress, // if processed no need to denied
            );
            return matchItem
                ? [
                      {
                          eventId: matchItem.conflictId,
                          itemId: item.approval_id,
                          itemTypeId: item.approval_type_id,
                          objectType: item.object_type,
                          objectId: item.object_id,
                          itemStateId: item.approval_state,
                          isTodo: false,
                      },
                  ]
                : [];
        });

        return matchedTasks;
    }
}
